import {
  useFormatZoned,
  useTimeAxisContext,
} from "utils/customHooks/genericHooks";
import { TAGHIGHLIGHT_BACKDROP_HEIGHT } from "../../HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import styled, { css } from "styled-components/macro";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import { highlight } from "constants/colors";
import {
  FORMAT_DAY_TIME_WITH_SECONDS,
  FORMAT_TIME_WITH_SECONDS,
} from "utils/timeUtils";
import { getIsSafari } from "constants/constants";

const SEGMENT_MARGIN = 2;

export function BelowTimelineSegment({
  range,
  title,
  subtitle = "",
  fillColor,
  textColor,
  y,
  serverNames,
  isHighlighted = false,
  customCss,
  className = "",
  icon = null,
  iconLeftmostOnly = false,
  tooltipTitle,
}: {
  range: { ge: number; lt?: number };
  title: string;
  subtitle?: string;
  fillColor: string;
  textColor: string;
  y: number;
  serverNames: (keyof typeof SERVER_NAMES)[];
  isHighlighted?: boolean;
  customCss?: string;
  className?: string;
  icon?: JSX.Element | null;
  iconLeftmostOnly?: boolean;
  tooltipTitle?: string | JSX.Element;
}) {
  const { xLeft, xRight, scaleTime } = useTimeAxisContext();

  const x0 = range.ge ? scaleTime(range.ge) || 0 : xLeft;
  const x1 = range.lt
    ? scaleTime(range.lt) || 0
    : Math.min(scaleTime(Date.now()), xRight);
  const rectLeft = Math.max(x0, xLeft);
  const rectRight = Math.min(x1, xRight);
  const rectWidth = rectRight - rectLeft;

  const { geReadable, ltReadable } = useReadableRange(range);
  const isLeftmost = x0 === 0;
  const doesIconFit = x1 - x0 > 35;
  const showIcon = doesIconFit && (!iconLeftmostOnly || isLeftmost);
  return (
    <StyledForeignObject
      className={className}
      $isHighlighted={isHighlighted}
      x={rectLeft + SEGMENT_MARGIN / 2}
      y={y}
      height={TAGHIGHLIGHT_BACKDROP_HEIGHT}
      width={Math.max(0, rectWidth - SEGMENT_MARGIN / 2)}
      data-server-names={serverNames}
      $customCss={customCss}
      $isLeftmost={isLeftmost}
    >
      <div
        className={"title" + (icon && showIcon ? "" : " no-icon")}
        style={{
          color: textColor,
          ...(title === "--" ? { paddingLeft: 20 } : {}),
        }}
      >
        {icon && showIcon && (
          <div className="icon" style={{ marginRight: 4 }}>
            {icon}
          </div>
        )}
        <div
          className="text"
          style={
            icon && iconLeftmostOnly && !isLeftmost
              ? {
                  margin: "auto",
                }
              : {}
          }
        >
          {title}
        </div>
      </div>
      <HtmlTooltip
        title={
          <div style={{ fontSize: 12 }}>
            <div style={{ textAlign: "center" }}>{tooltipTitle ?? title}</div>
            <div style={{ textAlign: "center", opacity: 0.6 }}>{subtitle}</div>
            <div style={{ textAlign: "center", opacity: 0.6, fontSize: 10 }}>
              {geReadable} - {ltReadable}
            </div>
          </div>
        }
        enterDelay={0}
        enterTouchDelay={0}
        disableInteractive={true}
      >
        <svg height={"100%"} width={"100%"}>
          <rect height={"100%"} width={"100%"} rx={2} ry={2} fill={fillColor} />
        </svg>
      </HtmlTooltip>
    </StyledForeignObject>
  );
}
const FONT_SIZE = 12;
export const StyledForeignObject = styled.foreignObject<{
  $isHighlighted: boolean;
  $isLeftmost: boolean;
  $customCss?: string;
}>`
  position: relative;
  transition: y 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  ${props =>
    props.$isHighlighted
      ? css`
          box-shadow: 0 0 0 2px ${highlight};
        `
      : ``}
  &:hover {
    opacity: 0.8;
  }
  .title {
    pointer-events: none;
    font-size: ${FONT_SIZE}px;
    padding-left: 8px;
    margin-top: 0;
    height: 100%;
    position: ${getIsSafari() ? "fixed" : "absolute"};
    left: 0;
    right: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;

    justify-content: center;
    ${props =>
      props.$isLeftmost
        ? css`
            &:not(.no-icon) {
              justify-content: flex-start !important;
              padding-left: 4px !important;
            }
            &.no-icon .text {
              letter-spacing: 0.1em;
            }
          `
        : ``}
    &.no-icon .text {
      margin: auto;
    }
    .icon {
      display: flex;
      align-items: center;
      width: 18px;
      height: 18px;
      margin-right: 4px;
      .MuiSvgIcon-root {
        width: 100%;
        height: 100%;
      }
    }
    .text {
      min-width: 0; /* needed for text-overfow: ellipsis inside of display: flex */
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: flex;
      justify-content: center;
    }
  }
  ${props => props.$customCss}
`;
function useReadableRange(range: { ge: number; lt?: number }): {
  geReadable: string;
  ltReadable: string;
} {
  const formatZoned = useFormatZoned();
  const isStartDaySameAsEndDay =
    range.ge &&
    range.lt &&
    new Date(range.ge).getDate() === new Date(range.lt ?? Date.now()).getDate();
  const geReadable = range.ge
    ? formatZoned(
        range.ge,
        isStartDaySameAsEndDay
          ? FORMAT_TIME_WITH_SECONDS
          : FORMAT_DAY_TIME_WITH_SECONDS
      )
    : "--";
  const ltReadable = range.lt
    ? formatZoned(
        range.lt,
        isStartDaySameAsEndDay
          ? FORMAT_TIME_WITH_SECONDS
          : FORMAT_DAY_TIME_WITH_SECONDS
      )
    : "--";
  return { geReadable, ltReadable };
}
